import { toast } from "sonner";
import { useReportsStore } from "../../store/reports/reportsStore";
import api from "../../utils/api";
import { removeLiteNotificationFromCache } from "../../features/notifications/liteQueries";
import { Button } from "../ui";

export interface CustomNotificationProps {
  title: string;
  body: string;
  image: string;
  url: string;
  icon: string;
  type: "report" | "notification";
  /** ID del documento de notificación (para marcar como leído en BD) */
  notificationId: string;
  /** ID del job de reporte (solo tipo "report"; para readedReport) */
  reportId?: string;
  /** ID que sonner asigna al toast, para cerrar solo este */
  toastId?: string | number;
}

const NOTIFICATION_ICONS = {
  "warning-red": { icon: "ri-error-warning-fill", color: "text-error", tile: "bg-error-100" },
  "warning-yellow": { icon: "ri-alert-fill", color: "text-warning", tile: "bg-warning-100" },
  "chat-blue": { icon: "ri-chat-3-fill", color: "text-info", tile: "bg-info-100" },
  "success-green": {
    icon: "ri-checkbox-circle-fill",
    color: "text-success",
    tile: "bg-success-100",
  },
} as const;

const FALLBACK_ICON = {
  icon: "ri-notification-3-fill",
  color: "text-primary-600",
  tile: "bg-primary-100",
};

const getIconData = (icon: string) =>
  NOTIFICATION_ICONS[icon as keyof typeof NOTIFICATION_ICONS] ?? FALLBACK_ICON;

export const CustomNotification = ({
  title,
  body,
  image,
  url,
  icon,
  type = "notification",
  notificationId,
  reportId,
  toastId,
}: CustomNotificationProps) => {
  const handleClose = () => {
    toast.dismiss(toastId);
  };

  const iconData = getIconData(icon);
  const readedReport = useReportsStore((state) => state.readedReport);

  const markNotificationAsRead = async () => {
    try {
      if (notificationId) {
        await api.put("/notifications-read", { notification_id: notificationId });
        removeLiteNotificationFromCache(notificationId);
      } else if (type === "report" && reportId) {
        await api.put("/notifications-read", { report_id: reportId });
      }
    } catch (err) {
      console.warn("Error marking notification as read:", err);
    }
  };

  const handleViewMore = async () => {
    await markNotificationAsRead();
    if (type === "report" && reportId) {
      await readedReport(reportId);
    }

    const isExternal = url.startsWith("http") && !url.startsWith(window.location.origin);
    if (isExternal) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      const path = url.startsWith("http") ? new URL(url).pathname : url;
      window.location.href = path;
    }
    handleClose();
  };

  return (
    <div className="pointer-events-auto w-full overflow-hidden rounded-2xl border border-line bg-card shadow-dropdown">
      {image && (
        <img
          src={image}
          alt=""
          className="h-32 w-full object-cover max-sm:hidden"
        />
      )}
      <div className="flex items-start gap-3 p-4">
        <div
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${iconData.tile}`}
        >
          <i className={`${iconData.icon} text-lg ${iconData.color}`} />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-display text-sm font-semibold leading-snug text-content">
            {title}
          </h4>
          <p className="mt-0.5 break-words text-sm leading-snug text-content-secondary">
            {body}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={handleViewMore}>
              Ver más
            </Button>
            <Button size="sm" variant="ghost" onClick={handleClose}>
              Cerrar
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Cerrar notificación"
          className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-content-muted transition-colors hover:bg-subtle hover:text-content"
        >
          <i className="ri-close-line text-base" />
        </button>
      </div>
    </div>
  );
};
